import { useCallback, useEffect, useState } from 'react';
import { Link, generatePath, useParams } from 'react-router-dom';
import { RouterPaths } from '../../app/appConstants.js';
import { useLangContext } from '../../providers/LangProvider/LangProviderContext.js';
import amountFormat from '../../utils/amountFormat.js';
import { getRequest } from '../../app/request.js';
import { getNetworkPartner, getNetworkReferrals } from './partnerNetworkApi.js';
import {
    NetworkPageStyled,
    NetworkSectionStyled,
    NetworkTableStyled,
    NetworkTableWrapStyled,
} from './styled/PartnerNetworkStyled';

function SubPartnerDetail() {
    const { partner_uid } = useParams();
    const { getLangPath } = useLangContext();
    const [partner, setPartner] = useState(null);
    const [referrals, setReferrals] = useState([]);
    const [children, setChildren] = useState([]);

    const load = useCallback(async () => {
        const [detail, refs] = await Promise.all([
            getNetworkPartner(partner_uid),
            getNetworkReferrals({ partner_uid, limit: 50 }),
        ]);
        setPartner(detail);
        setReferrals(refs.items || []);
        const childrenRes = await getRequest(`/partner/network/partners/${partner_uid}/children`);
        setChildren(childrenRes.children || []);
    }, [partner_uid]);

    useEffect(() => {
        load();
    }, [load]);

    if (!partner) {
        return null;
    }

    return (
        <NetworkPageStyled>
            <div className="page-header">
                <Link to={RouterPaths.network}>{getLangPath('back')}</Link>
                <h1 className="page-title">
                    {partner.full_name} (#{partner.partner_uid})
                </h1>
                <p className="page-desc">
                    {getLangPath('network.level')}: L{partner.level}
                    {partner.stats ? ` · ${getLangPath('network.col_payout')}: $${amountFormat(partner.stats.commission)}` : ''}
                </p>
                {partner.pending_ib_offer && (
                    <p className="page-desc">{getLangPath('network.pending_ib_offer')}</p>
                )}
            </div>

            <NetworkSectionStyled>
                <div className="section-header">
                    <div className="section-heading">
                        <h2 className="section-title">{getLangPath('network.sub_partners')}</h2>
                    </div>
                </div>
                <NetworkTableWrapStyled>
                    <NetworkTableStyled>
                        <tbody>
                            {children.length > 0 ? (
                                children.map((child) => (
                                    <tr key={child.partner_uid}>
                                        <td>
                                            <Link
                                                to={generatePath(RouterPaths.networkPartner, {
                                                    partner_uid: child.partner_uid,
                                                })}
                                            >
                                                {child.full_name} (#{child.partner_uid})
                                            </Link>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td className="empty-cell">{getLangPath('network.graph_empty')}</td>
                                </tr>
                            )}
                        </tbody>
                    </NetworkTableStyled>
                </NetworkTableWrapStyled>
            </NetworkSectionStyled>

            <NetworkSectionStyled>
                <div className="section-header">
                    <div className="section-heading">
                        <h2 className="section-title">{getLangPath('network.referrals_title')}</h2>
                    </div>
                </div>
                <NetworkTableWrapStyled>
                    <NetworkTableStyled>
                        <thead>
                            <tr>
                                <th>{getLangPath('network.click_id')}</th>
                                <th>{getLangPath('network.status')}</th>
                                <th className="col-money">{getLangPath('network.col_payout')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {referrals.length > 0 ? (
                                referrals.map((row) => (
                                    <tr key={row.click_id}>
                                        <td>{row.click_id}</td>
                                        <td>{row.status}</td>
                                        <td className="col-money">${amountFormat(row.commission)}</td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td className="empty-cell" colSpan={3}>
                                        {getLangPath('network.referrals_empty')}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </NetworkTableStyled>
                </NetworkTableWrapStyled>
            </NetworkSectionStyled>
        </NetworkPageStyled>
    );
}

export default SubPartnerDetail;
